import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Badge, Select } from '@tkpod/ui';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';

interface UptOptionValue {
  id: string;
  optionValueId: string;
  name: string;
  slug: string;
  displayName: string;
  metadata: Record<string, unknown>;
  swatchUrl: string | null;
  mockupImageUrl: string | null;
  surcharge: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface UptOption {
  id: string;
  uptId: string;
  optionTypeId: string;
  optionTypeName: string;
  optionTypeSlug: string;
  optionTypeDisplayName: string | null;
  uiType: string;
  position: number;
  isRequired: boolean;
  isActive: boolean;
  displayNameOverride: string | null;
  /** Override → OptionType displayName → name (what Shopify/designer shows). */
  resolvedDisplayName: string;
  values: UptOptionValue[];
}

interface OptionType {
  id: string;
  name: string;
  slug: string;
  displayName: string;
  uiType: string;
  valueCount: number;
  isActive: boolean;
}

interface OptionValue {
  id: string;
  optionTypeId: string;
  name: string;
  slug: string;
  displayName: string;
  metadata: Record<string, unknown>;
  sortOrder: number;
  isActive: boolean;
}

const MAX_OPTION_TYPES = 3;

export function VariantOptionPresets({
  templateId,
  onDirtyChange,
  onOptionChanged,
  onOptionCountChange,
}: {
  templateId: string;
  onDirtyChange?: (dirty: boolean) => void;
  onOptionChanged?: () => void;
  onOptionCountChange?: (count: number, hasValues: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [assignValueIds, setAssignValueIds] = useState<Set<string>>(new Set());
  const [addingValueFor, setAddingValueFor] = useState<string | null>(null);
  const [selectedValueIds, setSelectedValueIds] = useState<Set<string>>(new Set());
  const [confirmRemoveOption, setConfirmRemoveOption] = useState<string | null>(null);

  useEffect(() => {
    setSelectedValueIds(new Set());
  }, [addingValueFor]);

  const isDirty = showAssign || addingValueFor !== null;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const optionsQueryKey = ['upt-options', templateId];

  const { data: uptOptions = [], isLoading, isError, error } = useQuery<UptOption[]>({
    queryKey: optionsQueryKey,
    queryFn: () => api.get<UptOption[]>(`/api/v1/admin/options/upt/${templateId}`),
  });

  useEffect(() => {
    const hasValues = uptOptions.length > 0 && uptOptions.every((o) => o.values.length > 0);
    onOptionCountChange?.(uptOptions.length, hasValues);
  }, [uptOptions, onOptionCountChange]);

  const { data: globalTypes = [] } = useQuery<OptionType[]>({
    queryKey: ['option-types'],
    queryFn: () => api.get<OptionType[]>('/api/v1/admin/options/types?activeOnly=true'),
  });

  const { data: assignTypeDetail } = useQuery<{ id: string; values: OptionValue[] }>({
    queryKey: ['option-type-detail', selectedTypeId],
    queryFn: () => api.get(`/api/v1/admin/options/types/${selectedTypeId}`),
    enabled: !!selectedTypeId && showAssign,
  });

  const assignAvailableValues = (assignTypeDetail?.values ?? []).filter((v) => v.isActive);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: optionsQueryKey });

  const assignMutation = useMutation({
    mutationFn: (body: { optionTypeId: string; optionValueIds: string[] }) =>
      api.post(`/api/v1/admin/options/upt/${templateId}`, body),
    onSuccess: () => {
      invalidate();
      setShowAssign(false);
      setSelectedTypeId('');
      setAssignValueIds(new Set());
      toast('Option type assigned.');
      onOptionChanged?.();
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to assign option type.', 'error');
    },
  });

  const removeOptionMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/admin/options/upt-option/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmRemoveOption(null);
      toast('Option removed.');
      onOptionChanged?.();
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to remove option.', 'error');
    },
  });

  const addValueMutation = useMutation({
    mutationFn: ({ uptOptionId, body }: { uptOptionId: string; body: { optionValueIds: string[] } }) =>
      api.post(`/api/v1/admin/options/upt-option/${uptOptionId}/values/bulk`, body),
    onSuccess: () => {
      invalidate();
      setAddingValueFor(null);
      setSelectedValueIds(new Set());
      toast('Values added.');
      onOptionChanged?.();
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to add values.', 'error');
    },
  });

  const createCustomValueMutation = useMutation({
    mutationFn: ({ uptOptionId, body }: { uptOptionId: string; body: { name: string; displayName: string } }) =>
      api.post(`/api/v1/admin/options/upt-option/${uptOptionId}/values/create-custom`, body),
    onSuccess: () => {
      invalidate();
      setAddingValueFor(null);
      toast('Custom value created and assigned.');
      onOptionChanged?.();
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to create custom value.', 'error');
    },
  });


  const assignedTypeIds = new Set(uptOptions.map((o) => o.optionTypeId));
  const availableTypes = globalTypes.filter((t) => !assignedTypeIds.has(t.id));
  const atMaxOptionTypes = uptOptions.length >= MAX_OPTION_TYPES;

  const handleAssign = () => {
    if (!selectedTypeId || assignValueIds.size === 0) return;
    assignMutation.mutate({ optionTypeId: selectedTypeId, optionValueIds: Array.from(assignValueIds) });
  };

  return (
    <div>
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider">
            Variant Option Presets ({uptOptions.length}/{MAX_OPTION_TYPES})
          </h3>
          <Button
            size="sm"
            onClick={() => setShowAssign(true)}
            disabled={availableTypes.length === 0 || atMaxOptionTypes}
            title={atMaxOptionTypes ? `Maximum ${MAX_OPTION_TYPES} option types allowed` : undefined}
          >
            Add Variant
          </Button>
        </div>

        {showAssign && (
          <div className="mb-4 rounded-md border border-border p-4 space-y-3">
            <label className="text-xs font-medium text-text-secondary">Select Option Type</label>
            {availableTypes.length === 0 ? (
              <p className="text-sm text-text-tertiary">All option types have been assigned.</p>
            ) : (
              <Select
                value={selectedTypeId}
                onChange={(e) => { setSelectedTypeId(e.target.value); setAssignValueIds(new Set()); }}
                options={[
                  { value: '', label: 'Choose an option type...' },
                  ...availableTypes.map((t) => ({
                    value: t.id,
                    label: `${t.name} (${t.uiType.replace(/_/g, ' ')}, ${t.valueCount} values)`,
                  })),
                ]}
              />
            )}

            {selectedTypeId && assignAvailableValues.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-text-secondary">Select Values to Include</label>
                  <button
                    type="button"
                    className="text-xs text-brand-600 hover:text-brand-700"
                    onClick={() => {
                      if (assignValueIds.size === assignAvailableValues.length) {
                        setAssignValueIds(new Set());
                      } else {
                        setAssignValueIds(new Set(assignAvailableValues.map((v) => v.id)));
                      }
                    }}
                  >
                    {assignValueIds.size === assignAvailableValues.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-surface p-2 space-y-1">
                  {assignAvailableValues.map((v) => {
                    const hex = typeof (v.metadata as Record<string, unknown>)?.hexCode === 'string'
                      ? (v.metadata as Record<string, string>).hexCode
                      : null;
                    return (
                      <label
                        key={v.id}
                        className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors ${
                          assignValueIds.has(v.id)
                            ? 'bg-brand-50 text-brand-700'
                            : 'text-text-primary hover:bg-surface-secondary'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={assignValueIds.has(v.id)}
                          onChange={() => {
                            setAssignValueIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(v.id)) next.delete(v.id);
                              else next.add(v.id);
                              return next;
                            });
                          }}
                          className="rounded border-border"
                        />
                        {hex && (
                          <span
                            className="inline-block h-4 w-4 shrink-0 rounded border border-border"
                            style={{ backgroundColor: hex }}
                          />
                        )}
                        {v.displayName || v.name}
                      </label>
                    );
                  })}
                </div>
                {assignValueIds.size > 0 && (
                  <p className="text-xs text-text-secondary">
                    {assignValueIds.size} value{assignValueIds.size !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            )}

            {assignMutation.isError && (
              <div className="rounded-md bg-danger-50 p-2 text-sm text-danger-700">
                {(assignMutation.error as Error)?.message || 'Failed to assign option type.'}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleAssign}
                disabled={!selectedTypeId || assignValueIds.size === 0 || assignMutation.isPending}
              >
                {assignMutation.isPending ? 'Assigning...' : `Assign with ${assignValueIds.size} Value${assignValueIds.size !== 1 ? 's' : ''}`}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowAssign(false); setSelectedTypeId(''); setAssignValueIds(new Set()); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-md border border-border px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="h-4 w-4 rounded bg-surface-tertiary" />
                  <div className="h-4 w-28 rounded bg-surface-tertiary" />
                  <div className="h-5 w-20 rounded bg-surface-tertiary" />
                  <div className="h-5 w-14 rounded bg-surface-tertiary" />
                  <div className="h-3 w-24 rounded bg-surface-tertiary" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-danger-50">
              <svg className="h-5 w-5 text-danger-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-text-secondary">Failed to load options</p>
            <p className="mt-1 text-xs text-text-tertiary">{(error as Error)?.message || 'An unexpected error occurred.'}</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => invalidate()}>
              Retry
            </Button>
          </div>
        ) : uptOptions.length === 0 ? (
          <div className="py-12 text-center">
            <svg className="mx-auto h-10 w-10 text-text-tertiary/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0Z" />
            </svg>
            <p className="mt-3 text-sm font-medium text-text-secondary">No options assigned</p>
            <p className="mt-1 text-xs text-text-tertiary">Add variant options to this template.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {uptOptions.map((opt) => (
              <OptionAccordion
                key={opt.id}
                option={opt}
                expanded={expandedId === opt.id}
                onToggle={() => setExpandedId(expandedId === opt.id ? null : opt.id)}
                addingValueFor={addingValueFor}
                setAddingValueFor={setAddingValueFor}
                selectedValueIds={selectedValueIds}
                setSelectedValueIds={setSelectedValueIds}
                addValueMutation={addValueMutation}
                createCustomValueMutation={createCustomValueMutation}
                confirmRemoveOption={confirmRemoveOption}
                setConfirmRemoveOption={setConfirmRemoveOption}
                removeOptionMutation={removeOptionMutation}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Display Name Editor (per-template axis name shown on Shopify / designer)
   ═══════════════════════════════════════════════════════════════════════════ */

function DisplayNameEditor({ option }: { option: UptOption }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(option.displayNameOverride ?? '');

  useEffect(() => {
    setDraft(option.displayNameOverride ?? '');
  }, [option.displayNameOverride]);

  const fallbackName = option.optionTypeDisplayName || option.optionTypeName;
  const trimmed = draft.trim();
  const isDirty = trimmed !== (option.displayNameOverride ?? '');

  const saveMutation = useMutation({
    mutationFn: (displayNameOverride: string | null) =>
      api.patch(`/api/v1/admin/options/upt-option/${option.id}`, {
        displayNameOverride,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upt-options', option.uptId] });
      toast(
        'Display name updated. Republish the product for the change to appear on Shopify.',
      );
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to update display name.', 'error');
    },
  });

  return (
    <div className="mb-4 rounded-md border border-border bg-surface-secondary/40 p-3">
      <label className="block text-xs font-medium text-text-secondary mb-1">
        Display Name (shown on Shopify & designer)
      </label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={fallbackName}
          maxLength={100}
          className="w-56 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <Button
          size="sm"
          onClick={() => saveMutation.mutate(trimmed || null)}
          disabled={!isDirty || saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
        {option.displayNameOverride && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => saveMutation.mutate(null)}
            disabled={saveMutation.isPending}
          >
            Reset to “{fallbackName}”
          </Button>
        )}
      </div>
      <p className="mt-1.5 text-xs text-text-tertiary">
        Editable at any time. Leave empty to use the global option's display
        name (“{fallbackName}”). Changing this after publish requires a
        republish to update Shopify option names.
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Option Accordion
   ═══════════════════════════════════════════════════════════════════════════ */

type AddValueTab = 'existing' | 'create';

function OptionAccordion({
  option,
  expanded,
  onToggle,
  addingValueFor,
  setAddingValueFor,
  selectedValueIds,
  setSelectedValueIds,
  addValueMutation,
  createCustomValueMutation,
  confirmRemoveOption,
  setConfirmRemoveOption,
  removeOptionMutation,
}: {
  option: UptOption;
  expanded: boolean;
  onToggle: () => void;
  addingValueFor: string | null;
  setAddingValueFor: (id: string | null) => void;
  selectedValueIds: Set<string>;
  setSelectedValueIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  addValueMutation: { mutate: (args: { uptOptionId: string; body: { optionValueIds: string[] } }) => void; isPending: boolean; isError: boolean; error: Error | null };
  createCustomValueMutation: { mutate: (args: { uptOptionId: string; body: { name: string; displayName: string } }) => void; isPending: boolean; isError: boolean; error: Error | null };
  confirmRemoveOption: string | null;
  setConfirmRemoveOption: (id: string | null) => void;
  removeOptionMutation: { mutate: (id: string) => void; isPending: boolean };
}) {
  const assignedValueIds = new Set(option.values.map((v) => v.optionValueId));
  const [addValueTab, setAddValueTab] = useState<AddValueTab>('existing');
  const [customName, setCustomName] = useState('');
  const [customDisplayName, setCustomDisplayName] = useState('');

  const { data: optionTypeDetail } = useQuery<{
    id: string;
    values: OptionValue[];
  }>({
    queryKey: ['option-type-detail', option.optionTypeId],
    queryFn: () => api.get(`/api/v1/admin/options/types/${option.optionTypeId}`),
    enabled: expanded,
  });

  const availableValues = (optionTypeDetail?.values ?? []).filter(
    (v) => v.isActive && !assignedValueIds.has(v.id),
  );

  const handleAddValues = () => {
    if (selectedValueIds.size === 0) return;
    addValueMutation.mutate({
      uptOptionId: option.id,
      body: { optionValueIds: Array.from(selectedValueIds) },
    });
  };

  const handleCreateCustom = () => {
    const trimmedName = customName.trim();
    const trimmedDisplay = customDisplayName.trim();
    if (!trimmedName || !trimmedDisplay) {
      toast('Name and Display Name are required.', 'error');
      return;
    }
    createCustomValueMutation.mutate({
      uptOptionId: option.id,
      body: { name: trimmedName, displayName: trimmedDisplay },
    });
  };

  const toggleValueSelection = (id: string) => {
    setSelectedValueIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openAddPanel = () => {
    setAddingValueFor(option.id);
    setSelectedValueIds(new Set());
    setCustomName('');
    setCustomDisplayName('');
    setAddValueTab(availableValues.length > 0 ? 'existing' : 'create');
  };

  const closeAddPanel = () => {
    setAddingValueFor(null);
    setSelectedValueIds(new Set());
    setCustomName('');
    setCustomDisplayName('');
  };

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-surface-secondary/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`h-4 w-4 text-text-secondary transition-transform ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
          <span className="text-sm font-semibold text-text-primary">{option.optionTypeName}</span>
          {option.resolvedDisplayName &&
            option.resolvedDisplayName !== option.optionTypeName && (
              <span
                className="text-xs text-text-secondary"
                title="Display name shown on Shopify and in the designer"
              >
                → “{option.resolvedDisplayName}”
              </span>
            )}
          <Badge variant="default" size="sm">{option.uiType.replace(/_/g, ' ')}</Badge>
          <Badge variant={option.isActive ? 'success' : 'default'} size="sm">
            {option.isActive ? 'Active' : 'Inactive'}
          </Badge>
          <span className="text-xs text-text-secondary">
            {option.values.length} value{option.values.length !== 1 ? 's' : ''} · Position {option.position}
            {option.isRequired ? ' · Required' : ''}
          </span>
          {option.uiType === 'COLOR_SWATCH' && option.values.length > 0 && (
            <span className="flex items-center gap-0.5">
              {option.values.slice(0, 12).map((v) => {
                const hex = typeof (v.metadata as Record<string, unknown>)?.hexCode === 'string'
                  ? (v.metadata as Record<string, string>).hexCode
                  : null;
                return hex ? (
                  <span
                    key={v.id}
                    className="inline-block h-3.5 w-3.5 rounded border border-border"
                    style={{ backgroundColor: hex }}
                    title={v.displayName || v.name}
                  />
                ) : null;
              })}
              {option.values.length > 12 && (
                <span className="text-[10px] text-text-tertiary ml-0.5">+{option.values.length - 12}</span>
              )}
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border px-4 py-3">
          <DisplayNameEditor option={option} />

          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Assigned Values
            </h4>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={openAddPanel}
              >
                Add Value
              </Button>
              {confirmRemoveOption === option.id ? (
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => removeOptionMutation.mutate(option.id)}
                    disabled={removeOptionMutation.isPending}
                  >
                    {removeOptionMutation.isPending ? 'Removing...' : 'Confirm Remove'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmRemoveOption(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmRemoveOption(option.id)}
                >
                  Remove Option
                </Button>
              )}
            </div>
          </div>

          {addingValueFor === option.id && (
            <div className="mb-3 rounded-md border border-brand-200 bg-brand-50 p-3 space-y-3">
              {/* Tabs */}
              <div className="flex gap-1 border-b border-brand-200 pb-2">
                <button
                  type="button"
                  className={`px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${
                    addValueTab === 'existing'
                      ? 'bg-surface text-brand-700 border border-brand-200 border-b-surface -mb-[3px]'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                  onClick={() => setAddValueTab('existing')}
                >
                  Select Existing{availableValues.length > 0 ? ` (${availableValues.length})` : ''}
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${
                    addValueTab === 'create'
                      ? 'bg-surface text-brand-700 border border-brand-200 border-b-surface -mb-[3px]'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                  onClick={() => setAddValueTab('create')}
                >
                  Create New
                </button>
              </div>

              {addValueTab === 'existing' ? (
                <>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-text-secondary">Select Values to Add</label>
                    {availableValues.length > 0 && (
                      <button
                        type="button"
                        className="text-xs text-brand-600 hover:text-brand-700"
                        onClick={() => {
                          if (selectedValueIds.size === availableValues.length) {
                            setSelectedValueIds(new Set());
                          } else {
                            setSelectedValueIds(new Set(availableValues.map((v) => v.id)));
                          }
                        }}
                      >
                        {selectedValueIds.size === availableValues.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  {availableValues.length === 0 ? (
                    <p className="text-sm text-text-tertiary">All values already assigned. Use "Create New" to add a custom value.</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-surface p-2 space-y-1">
                      {availableValues.map((v) => {
                        const hex = option.uiType === 'COLOR_SWATCH' && typeof (v.metadata as Record<string, unknown>)?.hexCode === 'string'
                          ? (v.metadata as Record<string, string>).hexCode
                          : null;
                        return (
                          <label
                            key={v.id}
                            className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors ${
                              selectedValueIds.has(v.id)
                                ? 'bg-brand-50 text-brand-700'
                                : 'text-text-primary hover:bg-surface-secondary'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedValueIds.has(v.id)}
                              onChange={() => toggleValueSelection(v.id)}
                              className="rounded border-border"
                            />
                            {hex && (
                              <span
                                className="inline-block h-4 w-4 shrink-0 rounded border border-border"
                                style={{ backgroundColor: hex }}
                              />
                            )}
                            {v.displayName || v.name}
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {selectedValueIds.size > 0 && (
                    <p className="text-xs text-text-secondary">
                      {selectedValueIds.size} value{selectedValueIds.size !== 1 ? 's' : ''} selected
                    </p>
                  )}
                  {addValueMutation.isError && (
                    <div className="rounded-md bg-danger-50 p-2 text-sm text-danger-700">
                      {(addValueMutation.error as Error)?.message || 'Failed to add values.'}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleAddValues}
                      disabled={selectedValueIds.size === 0 || addValueMutation.isPending}
                    >
                      {addValueMutation.isPending ? 'Adding...' : `Add${selectedValueIds.size > 0 ? ` (${selectedValueIds.size})` : ''}`}
                    </Button>
                    <Button size="sm" variant="outline" onClick={closeAddPanel}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Name *</label>
                      <input
                        type="text"
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        placeholder="e.g. Midnight Blue"
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        maxLength={100}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Display Name *</label>
                      <input
                        type="text"
                        value={customDisplayName}
                        onChange={(e) => setCustomDisplayName(e.target.value)}
                        placeholder="e.g. Midnight Blue"
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        maxLength={100}
                      />
                    </div>
                  </div>
                  {createCustomValueMutation.isError && (
                    <div className="rounded-md bg-danger-50 p-2 text-sm text-danger-700">
                      {(createCustomValueMutation.error as Error)?.message || 'Failed to create value.'}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleCreateCustom}
                      disabled={!customName.trim() || !customDisplayName.trim() || createCustomValueMutation.isPending}
                    >
                      {createCustomValueMutation.isPending ? 'Creating...' : 'Create & Add'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={closeAddPanel}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {option.values.length === 0 ? (
            <p className="py-4 text-center text-sm text-text-tertiary">
              No values assigned. Click "Add Value" to assign option values.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-text-secondary">
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Display Name</th>
                    {option.uiType === 'COLOR_SWATCH' && (
                      <th className="px-3 py-2 font-medium">Swatch</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {option.values.map((v) => (
                    <tr key={v.id} className="border-b border-border last:border-0">
                      <td className="px-3 py-2 font-medium">
                        <div className="flex items-center gap-2">
                          {option.uiType === 'COLOR_SWATCH' && v.metadata && typeof (v.metadata as Record<string, unknown>).hexCode === 'string' && (
                            <span
                              className="inline-block h-4 w-4 shrink-0 rounded border border-border"
                              style={{ backgroundColor: (v.metadata as Record<string, string>).hexCode }}
                            />
                          )}
                          {v.name}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-text-secondary">{v.displayName}</td>
                      {option.uiType === 'COLOR_SWATCH' && (
                        <td className="px-3 py-2">
                          {v.swatchUrl ? (
                            <img src={v.swatchUrl} alt="Swatch" className="h-6 w-6 rounded border border-border object-cover" />
                          ) : (
                            <span className="text-text-tertiary">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
